/**
 * Navigation Configuration
 * RiskMetrica Views - Board Risk Committee Control Centre
 *
 * Defines all BRC routes and navigation structure
 */

import {
  LayoutDashboard,
  Shield,
  AlertTriangle,
  FileText,
  Target,
  TrendingUp,
  Activity,
  CheckCircle,
  Users,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  description?: string;
}

export interface NavigationSection {
  id: string;
  label: string;
  items: NavigationItem[];
}

export const NAVIGATION_CONFIG: NavigationSection[] = [
  {
    id: 'committee-core',
    label: 'Committee Core',
    items: [
      {
        id: 'overview',
        label: 'Overview',
        path: '/brc/overview',
        icon: LayoutDashboard,
        description: 'Executive dashboard and key metrics',
      },
      {
        id: 'arbiter-challenges',
        label: 'Arbiter and Challenges',
        path: '/brc/arbiter-challenges',
        icon: Shield,
        description: 'Arbiter challenges and constraint violations',
      },
      {
        id: 'actions-escalations',
        label: 'Actions and Escalations',
        path: '/brc/actions-escalations',
        icon: AlertTriangle,
        description: 'Management actions and escalations',
      },
      {
        id: 'committee-pack',
        label: 'Committee Pack',
        path: '/brc/committee-pack',
        icon: FileText,
        description: 'Board pack generation and export',
      },
    ],
  },
  {
    id: 'risk-domains',
    label: 'Risk Domains',
    items: [
      {
        id: 'risk-appetite',
        label: 'Risk Appetite',
        path: '/brc/risk-appetite',
        icon: Target,
        description: 'RAC statements and headroom analysis',
      },
      {
        id: 'scenario-resilience',
        label: 'Scenario & Resilience',
        path: '/brc/scenario-resilience',
        icon: TrendingUp,
        description: 'IRSA scenarios and impact tolerance',
      },
      {
        id: 'key-risk-indicators',
        label: 'Key Risk Indicators',
        path: '/brc/key-risk-indicators',
        icon: Activity,
        description: 'Dynamic indicators and forecasts',
      },
      {
        id: 'control-effectiveness',
        label: 'Control Effectiveness',
        path: '/brc/control-effectiveness',
        icon: CheckCircle,
        description: 'KCI performance and assurance',
      },
      {
        id: 'culture-conduct',
        label: 'Culture & Conduct',
        path: '/brc/culture-conduct',
        icon: Users,
        description: 'Risk Culture Integrity Index and conduct metrics',
      },
      {
        id: 'ai-insights',
        label: 'AI Insights',
        path: '/brc/ai-insights',
        icon: Sparkles,
        description: 'ML-driven forecasts and anomalies',
      },
    ],
  },
];

/**
 * Flat list of all navigation items for easy lookup
 */
export const ALL_NAVIGATION_ITEMS: NavigationItem[] = NAVIGATION_CONFIG.flatMap(
  (section) => section.items
);

/**
 * Get navigation item by path
 */
export function getNavigationItemByPath(path: string): NavigationItem | undefined {
  return ALL_NAVIGATION_ITEMS.find((item) => item.path === path);
}

/**
 * Get navigation section for a given path
 */
export function getNavigationSection(path: string): NavigationSection | undefined {
  return NAVIGATION_CONFIG.find((section) =>
    section.items.some((item) => item.path === path)
  );
}
